import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Select } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import DraggableCollectionRow from 'components/kea/DraggableCollectionRow';
import RowEditActions from 'components/kea/RowEditActions';
import { parseRankingSearchTerms, rankingSearchTermsValid } from '../model/rankingWorkbench';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useCompactPropertyFieldStyles } from '../../search-experience-v2/editor/properties/CompactPropertyFields';

const priorities = ['boosted', 'mid'];
const termKey = term => term.toLocaleLowerCase();
const distinctTerms = terms => [...new Map(terms.map(term => [termKey(term), term])).values()];
const draggableTermId = (priority, term) => `search-phrase:${priority}:${encodeURIComponent(termKey(term))}`;

export const moveSearchPhrase = (lists, source, destination) => {
  if (!destination || !priorities.includes(source.droppableId)
    || !priorities.includes(destination.droppableId)) return lists;
  const sourceTerms = [...lists[source.droppableId]];
  const term = sourceTerms[source.index];
  if (!term) return lists;
  sourceTerms.splice(source.index, 1);
  if (source.droppableId === destination.droppableId) {
    sourceTerms.splice(Math.min(destination.index, sourceTerms.length), 0, term);
    return { ...lists, [source.droppableId]: sourceTerms };
  }
  const destinationTerms = lists[destination.droppableId]
    .filter(item => termKey(item) !== termKey(term));
  destinationTerms.splice(Math.min(destination.index, destinationTerms.length), 0, term);
  return {
    ...lists,
    [source.droppableId]: sourceTerms,
    [destination.droppableId]: destinationTerms,
  };
};

const SearchPhraseEditor = ({ boostedTermsText, midTermsText, disabled, onChange }) => {
  const t = useLocalizedMessage();
  const compactClasses = useCompactPropertyFieldStyles();
  const [draft, setDraft] = useState('');
  const [priority, setPriority] = useState('boosted');
  const [error, setError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editing, setEditing] = useState(null);
  const editInput = useRef(null);
  const phraseEntry = useRef(null);
  const editButtons = useRef(new Map());
  const focusAfterEdit = useRef(null);
  const lists = {
    boosted: parseRankingSearchTerms(boostedTermsText),
    mid: parseRankingSearchTerms(midTermsText),
  };
  const priorityName = key => t(`merchandising.workbench.searchTerms.${key}`);
  useEffect(() => {
    if (editing && editInput.current) {
      editInput.current.focus();
      editInput.current.select();
    }
  }, [editing?.term]);
  useEffect(() => {
    if (!editing) return;
    const sourceText = editing.source === 'boosted' ? boostedTermsText : midTermsText;
    const sourceStillExists = parseRankingSearchTerms(sourceText)
      .some(term => termKey(term) === termKey(editing.term));
    if (!sourceStillExists) {
      focusAfterEdit.current = 'composer';
      setEditing(null);
      setEditError(null);
    }
  }, [boostedTermsText, editing?.source, editing?.term, midTermsText]);
  useEffect(() => {
    if (editing || !focusAfterEdit.current) return;
    const target = focusAfterEdit.current === 'composer'
      ? phraseEntry.current
      : editButtons.current.get(focusAfterEdit.current);
    const focusTarget = target || phraseEntry.current;
    if (focusTarget) focusTarget.focus();
    focusAfterEdit.current = null;
  });
  const commit = (next, setValidationError = setError) => {
    if (!priorities.every(key => rankingSearchTermsValid(next[key].join('\n')))) {
      setValidationError(t('merchandising.workbench.searchTermsLimit'));
      return false;
    }
    setError(null);
    setEditError(null);
    onChange({
      boostedTermsText: next.boosted.join('\n'),
      midTermsText: next.mid.join('\n'),
    });
    return true;
  };
  const add = () => {
    const incoming = parseRankingSearchTerms(draft);
    if (!incoming.length) return;
    if (!rankingSearchTermsValid(draft)) {
      setError(t('merchandising.workbench.searchTermsLimit'));
      return;
    }
    const incomingKeys = new Set(incoming.map(termKey));
    const next = {
      boosted: lists.boosted.filter(term => !incomingKeys.has(termKey(term))),
      mid: lists.mid.filter(term => !incomingKeys.has(termKey(term))),
    };
    next[priority] = distinctTerms([...next[priority], ...incoming]);
    if (commit(next)) setDraft('');
  };
  const remove = (term, source) => commit({
    ...lists,
    [source]: lists[source].filter(item => termKey(item) !== termKey(term)),
  });
  const startEditing = (term, source) => {
    setError(null);
    setEditError(null);
    setEditing({ term, source, value: term, priority: source });
  };
  const commitEditing = () => {
    if (!editing) return;
    const value = editing.value.trim();
    const duplicate = priorities.some(key => lists[key].some(term => (
      !(key === editing.source && termKey(term) === termKey(editing.term))
        && termKey(term) === termKey(value)
    )));
    if (!value || duplicate) {
      setEditError(t('empty.query.editor.row.edit.error'));
      return;
    }
    const next = {
      boosted: lists.boosted.filter(term => !(
        editing.source === 'boosted' && termKey(term) === termKey(editing.term)
      )),
      mid: lists.mid.filter(term => !(
        editing.source === 'mid' && termKey(term) === termKey(editing.term)
      )),
    };
    if (editing.priority === editing.source) {
      const originalIndex = lists[editing.source]
        .findIndex(term => termKey(term) === termKey(editing.term));
      next[editing.priority].splice(originalIndex, 0, value);
    } else {
      next[editing.priority].push(value);
    }
    if (commit(next, setEditError)) {
      focusAfterEdit.current = draggableTermId(editing.priority, value);
      setEditing(null);
    }
  };
  const cancelEditing = () => {
    if (!editing) return;
    focusAfterEdit.current = draggableTermId(editing.source, editing.term);
    setEditing(null);
    setEditError(null);
  };

  return (
    <section className="mp-search-phrases-section">
      <div className="mp-section-heading"><h2>{t('merchandising.workbench.searchTerms')}</h2></div>
      <div className="mp-phrase-composer">
        <textarea
          ref={phraseEntry}
          id="mp-phrase-entry"
          rows={Math.min(3, Math.max(1, draft.split('\n').length))}
          value={draft}
          disabled={disabled || Boolean(editing)}
          aria-label={t('merchandising.workbench.searchTermsAddLabel')}
          placeholder={t('merchandising.workbench.searchTermsPlaceholder')}
          onChange={event => { setDraft(event.target.value); setError(null); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              add();
            }
          }}
          aria-describedby={error ? 'mp-phrase-entry-error' : undefined}
          aria-invalid={Boolean(error)}
        />
        <Select
          className={`${compactClasses.select} mp-phrase-priority-control`}
          variant="outlined"
          value={priority}
          disabled={disabled || Boolean(editing)}
          inputProps={{ 'aria-label': t('merchandising.workbench.searchTermsChoosePriority') }}
          onChange={event => setPriority(event.target.value)}
          MenuProps={{
            classes: { paper: compactClasses.selectMenu },
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
        >
          {priorities.map(key => <MenuItem className={compactClasses.selectMenuItem} key={key} value={key}>{priorityName(key)}</MenuItem>)}
        </Select>
        <button type="button" className="mp-phrase-add-button" aria-label={t('merchandising.workbench.searchTermsAddAction')} onClick={add} disabled={disabled || Boolean(editing) || !draft.trim()}>
          <AddIcon aria-hidden="true" /><span>{t('merchandising.workbench.searchTermsAdd')}</span>
        </button>
        {error && <p className="mp-phrase-error" id="mp-phrase-entry-error" role="alert">{error}</p>}
      </div>
      <DragDropContext onDragEnd={({ source, destination }) => {
        if (!disabled) commit(moveSearchPhrase(lists, source, destination));
      }}>
        <div className="mp-search-phrase-priorities">
          {priorities.map(key => (
            <div className={`mp-search-phrase-lane mp-search-phrase-lane--${key}`} key={key}>
              <div className="mp-search-phrase-heading">
                <strong>{priorityName(key)} <span className="mp-phrase-count">{lists[key].length}</span></strong>
              </div>
              <Droppable droppableId={key} direction="horizontal" isDropDisabled={disabled || Boolean(editing)}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`mp-phrase-chip-list ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
                  >
                    {lists[key].length ? lists[key].map((term, index) => {
                      const isEditing = editing?.source === key
                        && termKey(editing.term) === termKey(term);
                      return (
                      <DraggableCollectionRow
                        key={draggableTermId(key, term)}
                        draggableId={draggableTermId(key, term)}
                        index={index}
                        disabled={disabled || Boolean(editing)}
                        portalWhileDragging
                        className={`mp-phrase-chip mp-phrase-chip--${key} ${isEditing ? 'is-editing' : ''}`}
                      >
                        {isEditing ? <>
                          <input
                            ref={editInput}
                            type="text"
                            className="mp-phrase-chip-input"
                            value={editing.value}
                            aria-label={t('empty.query.editor.row.edit.label', { term })}
                            aria-describedby={editError ? 'mp-phrase-edit-error' : undefined}
                            aria-invalid={Boolean(editError)}
                            onChange={event => {
                              setEditing({ ...editing, value: event.target.value });
                              setEditError(null);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') commitEditing();
                              if (event.key === 'Escape') cancelEditing();
                            }}
                          />
                          <Select
                            className={`${compactClasses.select} mp-phrase-edit-priority`}
                            variant="outlined"
                            value={editing.priority}
                            inputProps={{
                              'aria-label': t('search.fields.priority.ariaLabel', { field: term }),
                            }}
                            onChange={event => setEditing({
                              ...editing, priority: event.target.value,
                            })}
                            MenuProps={{
                              classes: { paper: compactClasses.selectMenu },
                              getContentAnchorEl: null,
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                            }}
                          >
                            {priorities.map(item => (
                              <MenuItem className={compactClasses.selectMenuItem} key={item} value={item}>{priorityName(item)}</MenuItem>
                            ))}
                          </Select>
                          <span className="mp-phrase-edit-actions">
                            <RowEditActions
                              confirmLabel={t('empty.query.editor.row.confirm')}
                              cancelLabel={t('empty.query.editor.row.cancel')}
                              onConfirm={commitEditing}
                              onCancel={cancelEditing}
                            />
                          </span>
                        </> : <>
                          <button
                            type="button"
                            className="mp-phrase-chip-label"
                            ref={(node) => {
                              const id = draggableTermId(key, term);
                              if (node) editButtons.current.set(id, node);
                              else editButtons.current.delete(id);
                            }}
                            disabled={disabled || Boolean(editing)}
                            aria-label={t('empty.query.editor.row.edit.label', { term })}
                            onClick={() => startEditing(term, key)}
                          >{term}</button>
                          {!disabled && !editing && (
                          <button
                            type="button"
                            className="mp-phrase-chip-remove"
                            onClick={() => remove(term, key)}
                            aria-label={t('merchandising.workbench.searchTermsRemove', { term })}
                          ><CloseIcon aria-hidden="true" /></button>
                          )}
                        </>}
                      </DraggableCollectionRow>
                      );
                    }) : <span className="mp-phrase-empty">{t('merchandising.workbench.searchTermsEmpty')}</span>}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      {editError && <p className="mp-phrase-error" id="mp-phrase-edit-error" role="alert">{editError}</p>}
    </section>
  );
};

SearchPhraseEditor.propTypes = {
  boostedTermsText: PropTypes.string,
  midTermsText: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
SearchPhraseEditor.defaultProps = {
  boostedTermsText: '',
  midTermsText: '',
  disabled: false,
};

export default SearchPhraseEditor;
